/**
 * BMR Details list columns (EXV-007 / 018 / 029). Names first, codes in tooltips; every row shows the server's
 * status word, tone, gate facts and QA disposition (BatchStatusCell); one date formatter (utils/datetime).
 */
import React from 'react';
import { Button, Tooltip } from 'antd';
import { Check, Eye, ScrollText, X } from 'lucide-react';
import BatchStatusCell from '@/components/batch/BatchStatusCell';
import { formatDateTime } from '@/utils/datetime';
import { BatchRowActions } from './BmrDetailsParts';

const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);

/** The batch number, its master record's name under it, and its lineage word (Master / Parent / Lot) from the server. */
const batchCell = (record) => {
  const lineage =
    record.batch_type === 'Lot' && record.parent_number
      ? `Lot of ${record.parent_number}`
      : record.batch_type === 'Parent' && record.lots?.length
        ? `Parent of ${record.lots.length} lot${record.lots.length === 1 ? '' : 's'}`
        : record.batch_type;
  return (
    <div className="bmrd-batch">
      <span className="bmrd-batch-no">{record.batch_number}</span>
      <span className="bmrd-sub">{[record.recipe_name, lineage].filter(Boolean).join(' · ')}</span>
    </div>
  );
};

const size = (record) => (record.batch_size === null || record.batch_size === undefined ? '—' : `${record.batch_size}${record.batch_size_unit ? ` ${record.batch_size_unit}` : ''}`);

const statusColumn = {
  title: 'Status',
  key: 'status',
  width: 260,
  render: (_, record) => {
    const detail = record?.pending_stage_detail;
    const cell = <BatchStatusCell record={record} />;
    return detail?.message ? <Tooltip title={detail.message}>{cell}</Tooltip> : cell;
  },
};

const scheduled = {
  title: 'Scheduled',
  dataIndex: 'batch_schedule_time',
  key: 'batch_schedule_time',
  width: 170,
  render: (v) => formatDateTime(v),
};

// Widths: the data columns are percentages of what the Actions column leaves (DataTable resolves them), so a wide screen
// spreads the data, not the icon column; Actions keeps a pixel width sized to its icons.
export const ongoingColumns = ({ recordWord, actions }) => [
  { title: recordWord, key: 'batch', width: '30%', render: (_, r) => batchCell(r) },
  { title: 'Product', dataIndex: 'product_name', key: 'product_name', width: '26%', render: dash },
  { ...statusColumn, width: '22%' },
  { ...scheduled, width: '14%' },
  { title: 'Size', key: 'size', width: '8%', render: (_, r) => size(r) },
  { title: 'Actions', key: 'actions', width: 140, render: (_, r) => <BatchRowActions record={r} {...actions} /> },
];

// W1-9: Approve / Reject are shown only to a person holding the act's own module flag (release_approval → "BMR Approval"
// create on the server, `canDecide`); the server still decides every act.
// EXV-ISSUE-2: each row names the role it waits on and since when; Approve / Reject show only on a row the caller can
// act on (`can_act`: the caller holds the routed role) — the server still decides every act.
export const waitingOnText = (r) => {
  const w = r?.waiting_on;
  if (!w?.role_name) return '—';
  return w.since ? `Waiting on ${w.role_name} · since ${formatDateTime(w.since)}` : `Waiting on ${w.role_name}`;
};

export const issueApprovalColumns = ({ recordWord, mbmrLabel, onApprove, onReject, onViewMbmr, canDecide = true }) => [
  { title: recordWord, key: 'batch', width: '24%', render: (_, r) => batchCell(r) },
  { title: 'Product', dataIndex: 'product_name', key: 'product_name', width: '16%', render: dash },
  { ...statusColumn, width: '18%' },
  {
    title: 'Waiting on',
    key: 'waiting_on',
    width: '18%',
    render: (_, r) => <span className="bmrd-sub" data-testid="bmrd-waiting-on">{waitingOnText(r)}</span>,
  },
  { ...scheduled, width: '12%' },
  { title: 'Size', key: 'size', width: '6%', render: (_, r) => size(r) },
  {
    title: 'Actions',
    key: 'actions',
    width: 140,
    render: (_, r) => (
      <div className="bmrd-actions">
        {canDecide && r.can_act !== false ? (
          <>
            <Tooltip title="Approve issue">
              <Button type="primary" icon={<Check size={16} />} onClick={() => onApprove(r)} aria-label={`Approve issue of ${r.batch_number}`} />
            </Tooltip>
            <Tooltip title="Reject issue">
              <Button danger icon={<X size={16} />} onClick={() => onReject(r)} aria-label={`Reject issue of ${r.batch_number}`} />
            </Tooltip>
          </>
        ) : null}
        {r.recipe_id ? (
          <Tooltip title={`View ${mbmrLabel}`}>
            <Button icon={<ScrollText size={16} />} onClick={() => onViewMbmr(r)} aria-label={`View ${mbmrLabel} of ${r.batch_number}`} />
          </Tooltip>
        ) : null}
      </div>
    ),
  },
];

export const rejectedColumns = ({ recordWord, onViewRecord }) => [
  { title: recordWord, key: 'batch', width: '26%', render: (_, r) => batchCell(r) },
  { ...statusColumn, width: '20%' },
  { title: 'Rejection reason', dataIndex: 'comment', key: 'comment', width: '22%', render: dash },
  {
    title: 'Rejected by',
    key: 'roles',
    width: '18%',
    render: (_, r) => dash((r.role_list || []).filter((x) => x.approval_status === 'reject').map((x) => x.role_name).join(', ')),
  },
  { ...scheduled, width: '14%' },
  {
    title: 'Actions',
    key: 'actions',
    width: 76,
    render: (_, r) => (
      <div className="bmrd-actions">
        <Tooltip title="View record">
          <Button icon={<Eye size={16} />} onClick={() => onViewRecord(r)} aria-label={`View record ${r.batch_number}`} />
        </Tooltip>
      </div>
    ),
  },
];
